import { useAppSelector } from "@app/hooks";
import { LFlex } from "@components";
import { convertUTCToLocalTimeZone } from "@helpers/dateHelpers";
import { NavItems } from "@helpers/navigation";
import { AssessmentResult, ExistingExamineeDetails } from "@store/generalTypes";
import { format } from "date-fns";
import { translate, Translate } from "react-i18nify";
import { NavLink } from "react-router-dom";
import { useLanguage } from "../../../../../lang/useLanguage";

export const ExamineeBanner = ({ ...props }: ExamineeBannerProps): JSX.Element => {
    // Silly trick to force re-render. Needed just for the translation of Form ¯\_(ツ)_/¯
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { language } = useLanguage();
    const location = useAppSelector((x) => x.orgLocations);
    const org = useAppSelector((x) => x.organization);

    const translatedFormName = props.assessment.assessmentFormName.replace("Form", translate("admin.form"));

    return (
        <div>
            <div className="mb-4">
                <NavLink to={NavItems.Admin.Results.parentRoot.path}>
                    &lt;{" "}
                    <span className="has-text-weight-bold">
                        <Translate value="admin.backToResults" />
                    </span>
                </NavLink>
            </div>
            <div className="columns has-text-ldark-gray">
                <div className="column is-half">
                    <h1 className="title">
                        {props.showDetails && (
                            <span>
                                {props.examinee.firstName} {props.examinee.lastName}
                            </span>
                        )}
                        {!props.showDetails && (
                            <span>
                                Examinee ID: <span className="has-text-weight-bold">{props.assessment.examineeId}</span>
                            </span>
                        )}
                    </h1>
                    <div>
                        <span>
                            <Translate value="admin.visit" />: {props.assessment.visitNumber}
                        </span>
                        <span className="is-inline-block mx-3 has-text-grey-light">&bull;</span>
                        <span>{translatedFormName}</span>
                    </div>
                    <div>
                        <span>
                            {format(convertUTCToLocalTimeZone(props.assessment.launchedDate), "MMM dd, yyyy h:mm aaa")}
                        </span>
                        <span className="is-inline-block mx-3 has-text-grey-light">&bull;</span>
                        <span>
                            <Translate value="admin.testingLanguage" />:{" "}
                            {props.assessment.languageName ?? <Translate value="admin.unknown" />}
                        </span>
                    </div>
                </div>
                <div className="column is-half has-text-right" style={{ marginTop: "auto" }}>
                    <LFlex row alignCenter className="is-justify-content-end">
                        <div className="no-wrap">
                            {props.assessment.facilitatorFirstName} {props.assessment.facilitatorLastName}
                            <br />
                            <Translate value="admin.assessmentFacilitator" />
                        </div>
                        <div className="has-text-grey-light mx-5">&bull;</div>
                        <div className="no-wrap">
                            {props.assessment.clinicianFirstName} {props.assessment.clinicianLastName}
                            <br />
                            <Translate value="admin.clinician" />
                        </div>
                    </LFlex>
                    <div className="mt-2">
                        <span className="is-inline-block mr-4">{location.currentLocation?.name}</span>
                        <span className="is-inline-block has-text-weight-bold ">{org.orgName}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export interface ExamineeBannerProps {
    assessment: AssessmentResult;
    examinee: ExistingExamineeDetails;
    showDetails: boolean;
}
